/**
 * Route Optimization Modal Component
 * 
 * Shows before/after comparison of route optimization with visual route comparison
 * and allows users to approve or reject the optimized route.
 */

import React, { useState, useMemo } from 'react';
import { View, StyleSheet, Modal, Pressable, ScrollView, ActivityIndicator, Alert } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ThemedText, SubHeading, Body, Caption } from '@/components/ui/Text';
import type { ItineraryItem } from '@/services/itinerary';
import { calculateOptimizedRoute } from '@/services/navigation';
import type { LocationCoordinates } from '@/services/location';
import { geocodeAddress } from '@/services/maps';
import { haversineDistance } from '@/utils/geocodeHelpers';
import { isAggressiveMapboxCostMode } from '@/config/mapboxCostPolicy';

type RouteOptimizationModalProps = {
  visible: boolean;
  itineraryItems: ItineraryItem[];
  startLocation?: LocationCoordinates;
  onClose: () => void;
  onApplyOptimization: (optimizedOrder: ItineraryItem[]) => Promise<void> | void;
};

type RouteComparison = {
  currentOrder: ItineraryItem[];
  optimizedOrder: ItineraryItem[];
  currentDistance: number; // in meters
  optimizedDistance: number; // in meters
  currentDuration: number; // in seconds
  optimizedDuration: number; // in seconds
  savings: {
    distance: number; // in meters
    duration: number; // in seconds
    percentage: number; // 0-100
  };
  changes: Array<{
    from: string;
    to: string;
    reason: string;
  }>;
};

export const RouteOptimizationModal: React.FC<RouteOptimizationModalProps> = ({
  visible,
  itineraryItems,
  startLocation,
  onClose,
  onApplyOptimization,
}) => {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [comparison, setComparison] = useState<RouteComparison | null>(null);
  const [error, setError] = useState<string | null>(null);
  const aggressiveCostMode = isAggressiveMapboxCostMode();

  const getDayKey = (date: Date) => date.toISOString().split('T')[0];
  const MAX_REASONABLE_KM_FROM_ANCHOR = 250; // guardrail: day optimization should stay city-scale

  const pickOptimizationWindow = (items: ItineraryItem[]) => {
    // Optimize a single day at a time (more useful + avoids Mapbox coord limits).
    const withDates = items.filter((i) => i.startDate);
    const byDay = withDates.reduce<Record<string, ItineraryItem[]>>((acc, item) => {
      const key = getDayKey(new Date(item.startDate));
      acc[key] = acc[key] ? [...acc[key], item] : [item];
      return acc;
    }, {});

    const todayKey = getDayKey(new Date());
    const sortedKeys = Object.keys(byDay).sort();
    const candidateKey = sortedKeys.find((k) => k >= todayKey && byDay[k].length >= 3) ??
      sortedKeys.find((k) => byDay[k].length >= 3) ??
      null;

    if (!candidateKey) return { dayKey: null as string | null, items: items };
    return { dayKey: candidateKey, items: byDay[candidateKey] };
  };

  const sortByStartTime = (items: ItineraryItem[]) => {
    const parseTime = (t?: string | null) => {
      const v = (t ?? '').trim();
      if (!v || v === 'flexible') return 0;
      const m = v.match(/^(\d{1,2}):(\d{2})/);
      if (!m) return 0;
      return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
    };
    return items
      .slice()
      .sort((a, b) => {
        const da = new Date(a.startDate).getTime();
        const db = new Date(b.startDate).getTime();
        if (da !== db) return da - db;
        return parseTime(a.startTime) - parseTime(b.startTime);
      });
  };

  const computeNearestNeighborOrder = (
    items: Array<{ item: ItineraryItem; coordinates: LocationCoordinates }>,
    start: LocationCoordinates,
  ) => {
    const remaining = items.slice();
    const ordered: typeof remaining = [];
    let current = start;

    while (remaining.length) {
      let bestIdx = 0;
      let best = Number.POSITIVE_INFINITY;
      for (let i = 0; i < remaining.length; i++) {
        const cand = remaining[i].coordinates;
        const d = haversineDistance(
          { lat: current.latitude, lng: current.longitude },
          { lat: cand.latitude, lng: cand.longitude },
        );
        if (d < best) {
          best = d;
          bestIdx = i;
        }
      }
      const next = remaining.splice(bestIdx, 1)[0];
      ordered.push(next);
      current = next.coordinates;
    }
    return ordered;
  };

  // Calculate optimization when modal opens
  React.useEffect(() => {
    if (visible && itineraryItems.length >= 3 && !comparison && !loading) {
      calculateOptimization();
    }
  }, [visible, itineraryItems.length]);

  const calculateOptimization = async () => {
    if (itineraryItems.length < 3) {
      setError('Need at least 3 activities to optimize');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Optimize one day at a time (more meaningful and avoids API limits).
      const { dayKey, items: windowItems } = pickOptimizationWindow(itineraryItems);
      const allowedCategories: ItineraryItem['category'][] = [
        'activity',
        'restaurant',
        'shopping',
        'other',
      ];
      const excludedCategories: ItineraryItem['category'][] = [
        'flight',
        'train',
        'ferry',
        'transportation',
        'car',
        'hotel',
      ];

      // Only optimize “things you visit” within a day/city. Travel + lodging are anchors, not reorderable stops.
      const filteredWindow = windowItems.filter((i) => {
        if (excludedCategories.includes(i.category)) return false;
        if (!allowedCategories.includes(i.category)) return false;
        return true;
      });

      const itemsToOptimize = sortByStartTime(filteredWindow);

      // Cap to keep the workflow fast and within Mapbox limits if we choose to call it.
      const maxStops = aggressiveCostMode ? (startLocation ? 6 : 7) : (startLocation ? 11 : 12);
      const cappedItems = itemsToOptimize.slice(0, maxStops);
      if (itemsToOptimize.length > maxStops) {
        console.warn('[RouteOptimization] Capping stops for optimization', {
          total: itemsToOptimize.length,
          using: maxStops,
        });
      }

      // Get coordinates for selected items
      const itemsWithCoords: Array<{ item: ItineraryItem; coordinates: LocationCoordinates }> = [];
      
      for (const item of cappedItems) {
        const address = item.address || item.location || item.title;
        if (!address) continue;

        try {
          const geocodeResult = await geocodeAddress(address, startLocation ? { proximity: startLocation } : undefined);
          if (geocodeResult) {
            const [lng, lat] = geocodeResult.coordinates;
            if (startLocation) {
              const kmFromAnchor = haversineDistance(
                { lat, lng },
                { lat: startLocation.latitude, lng: startLocation.longitude },
              );
              // If a geocode resolves far away, it's almost certainly the wrong place for this day/city.
              if (kmFromAnchor > MAX_REASONABLE_KM_FROM_ANCHOR) {
                if (__DEV__) {
                  console.warn(
                    `[RouteOptimization] Skipping outlier geocode for "${item.title}" (${Math.round(kmFromAnchor)}km from anchor)`,
                    { address, placeName: geocodeResult.placeName },
                  );
                }
                continue;
              }
            }
            itemsWithCoords.push({
              item,
              coordinates: { latitude: lat, longitude: lng },
            });
          }
        } catch (err) {
          console.warn(`[RouteOptimization] Failed to geocode ${item.title}:`, err);
        }
      }

      if (itemsWithCoords.length < 3) {
        setError('Not enough in-city activities with valid locations to optimize. Add addresses, or try a different day.');
        setLoading(false);
        return;
      }

      // Calculate current route (in order)
      let currentTotalDistance = 0;
      let currentTotalDuration = 0;
      const currentRoute = itemsWithCoords.map(({ coordinates }) => coordinates);

      if (startLocation) {
        currentRoute.unshift(startLocation);
      }

      const addHeuristicSegment = (from: LocationCoordinates, to: LocationCoordinates) => {
        const distanceKm = haversineDistance(
          { lat: from.latitude, lng: from.longitude },
          { lat: to.latitude, lng: to.longitude },
        );
        const distanceMeters = distanceKm * 1000;
        const durationSeconds = Math.max(180, Math.round((distanceKm / 28) * 3600));
        return { distanceMeters, durationSeconds };
      };

      for (let i = 0; i < currentRoute.length - 1; i++) {
        if (aggressiveCostMode) {
          const seg = addHeuristicSegment(currentRoute[i], currentRoute[i + 1]);
          currentTotalDistance += seg.distanceMeters;
          currentTotalDuration += seg.durationSeconds;
          continue;
        }
        try {
          const { calculateDetailedRoute } = await import('@/services/navigation');
          const route = await calculateDetailedRoute(currentRoute[i], currentRoute[i + 1], 'driving');
          if (route) {
            currentTotalDistance += route.distance;
            currentTotalDuration += route.duration;
          }
        } catch (err) {
          console.warn(`[RouteOptimization] Failed to calculate route segment:`, err);
        }
      }

      // Compute an optimized order (nearest-neighbor heuristic).
      // This is deterministic, fast, and doesn’t hit Mapbox coordinate limits.
      const startForOptimization = startLocation ?? itemsWithCoords[0].coordinates;
      const remainingForOptimization = startLocation ? itemsWithCoords : itemsWithCoords.slice(1);
      const optimizedPairs = computeNearestNeighborOrder(remainingForOptimization, startForOptimization);
      const optimizedOrderItems = startLocation
        ? optimizedPairs.map((p) => p.item)
        : [itemsWithCoords[0].item, ...optimizedPairs.map((p) => p.item)];

      // Try Mapbox optimized-trips API for a better total distance estimate (only when within limits).
      // If it returns null (e.g. too many coords), we fall back to summing segment directions.
      let optimizedRoute: LocationCoordinates[] = [];
      let optimizedTotalDistance = 0;
      let optimizedTotalDuration = 0;

      if (!aggressiveCostMode && startLocation && itemsWithCoords.length >= 2) {
        const coords = itemsWithCoords.map((p) => p.coordinates);
        const end = coords[coords.length - 1];
        const optimizedWaypoints = coords.slice(0, -1);
        const optimized = await calculateOptimizedRoute(startLocation, optimizedWaypoints, end, 'driving');
        if (optimized) {
          optimizedRoute = optimized.geometry;
          optimizedTotalDistance = optimized.distance;
          optimizedTotalDuration = optimized.duration;
        }
      }

      if (!optimizedTotalDistance) {
        // Fallback: sum driving directions over the heuristic order.
        const routeCoords = (startLocation ? [startLocation] : [itemsWithCoords[0].coordinates]).concat(
          optimizedPairs.map((p) => p.coordinates),
        );
        if (aggressiveCostMode) {
          for (let i = 0; i < routeCoords.length - 1; i++) {
            const seg = addHeuristicSegment(routeCoords[i], routeCoords[i + 1]);
            optimizedTotalDistance += seg.distanceMeters;
            optimizedTotalDuration += seg.durationSeconds;
          }
        } else {
          try {
            const { calculateDetailedRoute } = await import('@/services/navigation');
            for (let i = 0; i < routeCoords.length - 1; i++) {
              const seg = await calculateDetailedRoute(routeCoords[i], routeCoords[i + 1], 'driving');
              if (seg) {
                optimizedTotalDistance += seg.distance;
                optimizedTotalDuration += seg.duration;
              }
            }
          } catch (err) {
            console.warn('[RouteOptimization] Failed to calculate optimized segment sum:', err);
          }
        }
      }

      // Calculate savings
      const rawDistanceSavings = currentTotalDistance - optimizedTotalDistance;
      const rawDurationSavings = currentTotalDuration - optimizedTotalDuration;
      const distanceSavings = Math.max(0, rawDistanceSavings);
      const durationSavings = Math.max(0, rawDurationSavings);
      const distancePercentage = currentTotalDistance > 0 && distanceSavings > 0
        ? (distanceSavings / currentTotalDistance) * 100
        : 0;

      // Generate change descriptions
      const changes: Array<{ from: string; to: string; reason: string }> = [];
      const currentSeq = itemsToOptimize.map((i) => i.title).join(' → ');
      const optimizedSeq = optimizedOrderItems.map((i) => i.title).join(' → ');
      const isImprovement =
        (distanceSavings > 0 || durationSavings > 0) && currentSeq !== optimizedSeq;

      if (itemsWithCoords.length > 1 && isImprovement) {
        changes.push({
          from: currentSeq,
          to: optimizedSeq,
          reason: 'Reordered activities to minimize backtracking and travel time',
        });
      } else if (itemsWithCoords.length > 1 && currentSeq === optimizedSeq) {
        changes.push({
          from: currentSeq,
          to: optimizedSeq,
          reason: 'Current order already looks optimal for this day',
        });
      } else if (itemsWithCoords.length > 1 && !isImprovement) {
        changes.push({
          from: currentSeq,
          to: optimizedSeq,
          reason: 'No meaningful improvement found (likely due to travel legs, missing addresses, or a day that is already efficient)',
        });
      }

      setComparison({
        currentOrder: itemsToOptimize,
        optimizedOrder: optimizedOrderItems,
        currentDistance: currentTotalDistance,
        optimizedDistance: optimizedTotalDistance,
        currentDuration: currentTotalDuration,
        optimizedDuration: optimizedTotalDuration,
        savings: {
          distance: distanceSavings,
          duration: durationSavings,
          percentage: distancePercentage,
        },
        changes,
      });
    } catch (err) {
      console.error('[RouteOptimization] Error:', err);
      setError(err instanceof Error ? err.message : 'Failed to optimize route');
    } finally {
      setLoading(false);
    }
  };

  const formatDistance = (meters: number): string => {
    if (meters < 1000) {
      return `${Math.round(meters)} m`;
    }
    return `${(meters / 1000).toFixed(1)} km`;
  };

  const formatDuration = (seconds: number): string => {
    const minutes = Math.round(seconds / 60);
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const handleApply = async () => {
    try {
      if (!comparison || !comparison.optimizedOrder || comparison.optimizedOrder.length === 0) {
        Alert.alert('Unable to apply', 'No optimized order was generated.');
        return;
      }
      const canApply =
        (comparison.savings.distance > 0 || comparison.savings.duration > 0) &&
        comparison.currentOrder.map((i) => i.id).join(',') !== comparison.optimizedOrder.map((i) => i.id).join(',');
      if (!canApply) {
        Alert.alert('No optimization to apply', 'Your current order is already efficient (or we lacked reliable locations).');
        return;
      }
      setApplying(true);
      await onApplyOptimization(comparison.optimizedOrder);
      onClose();
    } catch (err) {
      Alert.alert('Unable to apply', 'Something went wrong applying the optimized order.');
      console.error('[RouteOptimization] Apply error:', err);
    } finally {
      setApplying(false);
    }
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
    >
      <Pressable 
        style={[styles.overlay, { backgroundColor: theme.colors.modalOverlay }]} 
        onPress={onClose}
      >
        <Pressable
          style={[
            styles.content,
            {
              backgroundColor: theme.colors.surface,
              borderTopLeftRadius: theme.radii.xl,
              borderTopRightRadius: theme.radii.xl,
              paddingBottom: Math.max(insets.bottom, 12),
            },
          ]}
          onPress={(e) => e.stopPropagation()}
        >
          {/* Drag handle */}
          <View style={[styles.dragHandle, { backgroundColor: theme.colors.borderMuted }]} />

          <View style={styles.header}>
            <View style={styles.headerLeft}>
              <View
                style={[
                  styles.iconContainer,
                  { backgroundColor: theme.colors.primary + '15' },
                ]}
              >
                <Ionicons
                  name="swap-horizontal"
                  size={24}
                  color={theme.colors.primary}
                />
              </View>
              <View style={styles.headerText}>
                <SubHeading style={styles.title}>Route Optimization</SubHeading>
                <Caption color={theme.colors.textSecondary}>
                  Optimize your route to save time and distance
                </Caption>
              </View>
            </View>
            <Pressable onPress={onClose} style={styles.closeButton}>
              <Ionicons
                name="close"
                size={24}
                color={theme.colors.textSecondary}
              />
            </Pressable>
          </View>

          <ScrollView
            style={styles.scrollView}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={false}
          >
            {loading && (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={theme.colors.primary} />
                <Body color={theme.colors.textSecondary} style={styles.loadingText}>
                  Calculating optimized route...
                </Body>
              </View>
            )}

            {error && (
              <Card variant="outlined" style={styles.errorCard}>
                <View style={styles.errorContent}>
                  <Ionicons name="alert-circle" size={24} color={theme.colors.error} />
                  <Body color={theme.colors.error}>{error}</Body>
                </View>
              </Card>
            )}

            {comparison && !loading && (
              <>
                {/* Savings Summary */}
                <Card variant="filled" style={[styles.summaryCard, { backgroundColor: theme.colors.primaryContainer || theme.colors.primary + '10' }]}>
                  <View style={styles.summaryRow}>
                    <View style={styles.summaryItem}>
                      <Ionicons name="checkmark-circle" size={24} color={theme.colors.success || '#10B981'} />
                      <View style={styles.summaryText}>
                        <Caption color={theme.colors.textSecondary}>Distance Saved</Caption>
                        <SubHeading style={styles.summaryValue}>
                          {formatDistance(comparison.savings.distance)}
                        </SubHeading>
                      </View>
                    </View>
                    <View style={styles.summaryItem}>
                      <Ionicons name="time" size={24} color={theme.colors.success || '#10B981'} />
                      <View style={styles.summaryText}>
                        <Caption color={theme.colors.textSecondary}>Time Saved</Caption>
                        <SubHeading style={styles.summaryValue}>
                          {formatDuration(comparison.savings.duration)}
                        </SubHeading>
                      </View>
                    </View>
                  </View>
                  {comparison.savings.percentage > 0 && (
                    <Body style={styles.percentageText}>
                      {comparison.savings.percentage.toFixed(0)}% more efficient
                    </Body>
                  )}
                </Card>

                {/* Comparison */}
                <View style={styles.comparisonSection}>
                  <Body style={styles.sectionTitle}>Route Comparison</Body>
                  
                  <View style={styles.comparisonRow}>
                    <View style={styles.comparisonColumn}>
                      <Caption color={theme.colors.textSecondary}>Current</Caption>
                      <Body style={styles.comparisonValue}>
                        {formatDistance(comparison.currentDistance)}
                      </Body>
                      <Caption color={theme.colors.textSecondary}>
                        {formatDuration(comparison.currentDuration)}
                      </Caption>
                    </View>
                    <Ionicons name="arrow-forward" size={20} color={theme.colors.textSecondary} />
                    <View style={styles.comparisonColumn}>
                      <Caption color={theme.colors.textSecondary}>Optimized</Caption>
                      <Body style={[styles.comparisonValue, { color: theme.colors.success || '#10B981' }]}>
                        {formatDistance(comparison.optimizedDistance)}
                      </Body>
                      <Caption color={theme.colors.textSecondary}>
                        {formatDuration(comparison.optimizedDuration)}
                      </Caption>
                    </View>
                  </View>
                </View>

                {/* Changes */}
                {comparison.changes.length > 0 && (
                  <View style={styles.changesSection}>
                    <Body style={styles.sectionTitle}>What Changed</Body>
                    {comparison.changes.map((change, index) => (
                      <View key={index} style={styles.changeItem}>
                        <Ionicons name="swap-horizontal-outline" size={16} color={theme.colors.primary} />
                        <View style={{ flex: 1, gap: 4 }}>
                          <Body style={styles.changeText}>{change.reason}</Body>
                          <Caption color={theme.colors.textSecondary} numberOfLines={2}>
                            From: {change.from}
                          </Caption>
                          <Caption color={theme.colors.textSecondary} numberOfLines={2}>
                            To: {change.to}
                          </Caption>
                        </View>
                      </View>
                    ))}
                  </View>
                )}
              </>
            )}
          </ScrollView>

          {/* Actions */}
          {comparison && !loading && (
            <View style={[styles.actions, { borderTopColor: theme.colors.borderMuted }]}>
              <Button
                label="Keep Current"
                variant="secondary"
                onPress={onClose}
                style={styles.actionButton}
              />
              <Button
                label={applying ? 'Applying...' : 'Apply Optimization'}
                onPress={handleApply}
                style={styles.actionButton}
                disabled={
                  applying ||
                  !(
                    (comparison.savings.distance > 0 || comparison.savings.duration > 0) &&
                    comparison.currentOrder.map((i) => i.id).join(',') !== comparison.optimizedOrder.map((i) => i.id).join(',')
                  )
                }
                icon={<Ionicons name="checkmark" size={20} color={theme.colorScheme === 'dark' ? '#000000' : theme.colors.surface} />}
              />
            </View>
          )}
        </Pressable>
      </Pressable>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  content: {
    // Use a fixed height (not maxHeight) so it actually opens “all the way up”.
    // maxHeight alone will shrink to content, which can look like a collapsed sheet.
    height: '92%',
    minHeight: '70%',
    paddingBottom: 0,
  },
  dragHandle: {
    width: 40,
    height: 4,
    borderRadius: 2,
    alignSelf: 'center',
    marginTop: 12,
    marginBottom: 8,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingHorizontal: 16,
    paddingTop: 8,
    paddingBottom: 16,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerText: {
    flex: 1,
    gap: 4,
  },
  title: {
    margin: 0,
    fontSize: 20,
  },
  closeButton: {
    padding: 4,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    gap: 16,
  },
  loadingContainer: {
    alignItems: 'center',
    paddingVertical: 40,
    gap: 12,
  },
  loadingText: {
    marginTop: 8,
  },
  errorCard: {
    padding: 16,
  },
  errorContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  summaryCard: {
    padding: 16,
    gap: 12,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  summaryItem: {
    alignItems: 'center',
    gap: 8,
  },
  summaryText: {
    alignItems: 'center',
    gap: 4,
  },
  summaryValue: {
    fontSize: 20,
    fontWeight: '700',
  },
  percentageText: {
    textAlign: 'center',
    fontWeight: '600',
    marginTop: 4,
  },
  comparisonSection: {
    gap: 12,
  },
  sectionTitle: {
    fontWeight: '600',
    fontSize: 16,
    marginBottom: 4,
  },
  comparisonRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-around',
    paddingVertical: 16,
  },
  comparisonColumn: {
    alignItems: 'center',
    gap: 4,
  },
  comparisonValue: {
    fontSize: 18,
    fontWeight: '700',
  },
  changesSection: {
    gap: 12,
  },
  changeItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 8,
  },
  changeText: {
    flex: 1,
  },
  actions: {
    flexDirection: 'row',
    gap: 12,
    padding: 16,
    paddingTop: 12,
    borderTopWidth: 1,
  },
  actionButton: {
    flex: 1,
  },
});

